/**
 * Noctura — apparition douce des blocs au scroll.
 * - Ne cache que ce qui est sous la ligne de flottaison : rien ne clignote au chargement.
 * - Les éléments répétés (cartes, produits, étapes…) apparaissent en cascade.
 * - Sans JS, ou avec « réduire les animations » activé, tout s'affiche normalement.
 */
(() => {
  const html = document.documentElement;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const STAGGER = 90; // ms entre deux cartes d'une même rangée
  const MAX_STAGGER = 5;
  const SKIP_SECTIONS = '[id*="cookie"], [id*="breadcrumbs"]';
  const SKIP_INSIDE = 'sticky-add-to-cart, .product-information, media-gallery';
  const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'META', 'NOSCRIPT', 'DIALOG']);

  const isRendered = (el) => {
    if (IGNORED_TAGS.has(el.tagName) || el.hasAttribute('hidden')) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (cs.position === 'absolute' || cs.position === 'fixed') return false;
    return el.offsetHeight > 0;
  };

  // Les enveloppes en « display: contents » n'ont pas de boîte : on prend leurs enfants
  const kidsOf = (el) =>
    Array.from(el.children).flatMap((k) =>
      !IGNORED_TAGS.has(k.tagName) && getComputedStyle(k).display === 'contents' ? kidsOf(k) : isRendered(k) ? [k] : []
    );

  const signature = (el) => `${el.tagName}.${el.classList[0] || ''}`;

  // 3 éléments frères ou plus, de même nature et d'une taille raisonnable = une rangée de cartes
  const isRepeated = (kids) =>
    kids.length >= 3 &&
    kids.every((k) => signature(k) === signature(kids[0]) && k.offsetHeight >= 40);

  const containsRepeated = (el, depth = 0) => {
    if (depth > 5) return false;
    const kids = kidsOf(el);
    if (isRepeated(kids)) return true;
    return kids.some((k) => containsRepeated(k, depth + 1));
  };

  // Premier descendant qui n'est pas une simple enveloppe à un seul enfant
  const unwrap = (el) => {
    let kids = kidsOf(el);
    for (let i = 0; kids.length === 1 && i < 8; i++) kids = kidsOf(kids[0]);
    return kids;
  };

  const units = [];
  const collect = (el, depth = 0) => {
    let kids = kidsOf(el);
    // On traverse les simples enveloppes (un seul enfant)
    while (kids.length === 1 && depth < 8) {
      el = kids[0];
      kids = kidsOf(el);
      depth++;
    }
    if (!kids.length) return units.push({ el, group: null });
    if (isRepeated(kids)) return kids.forEach((k) => units.push({ el: k, group: el }));
    kids.forEach((k) => {
      // On descend dans les grands blocs (texte + image + bouton…) pour animer chaque élément
      const tall = k.offsetHeight > window.innerHeight * 0.5 && unwrap(k).length >= 2;
      if (depth < 8 && (tall || containsRepeated(k))) collect(k, depth + 1);
      else units.push({ el: k, group: null });
    });
  };

  const reveal = (el, delay) => {
    el.style.setProperty('--nc-delay', `${delay}ms`);
    el.classList.add('nc-in');
    const done = () => {
      el.removeAttribute('data-nc-reveal');
      el.querySelectorAll('[data-nc-zoom]').forEach((img) => img.removeAttribute('data-nc-zoom'));
      el.classList.remove('nc-in');
      el.style.removeProperty('--nc-delay');
    };
    // Nettoyage après l'animation (le plus long : le dézoom des images)
    setTimeout(done, delay + 1400);
  };

  const members = new Map(); // conteneur de cartes -> cartes à animer

  const observer = new IntersectionObserver(
    (entries) => {
      const counters = new Map();
      entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
        .forEach(({ target }) => {
          const group = target.__ncGroup;
          if (!group) {
            observer.unobserve(target);
            return reveal(target, 0);
          }
          // Toute la rangée part ensemble, y compris les cartes masquées sur le côté d'un carrousel
          const row = (members.get(group) || [target]).filter((el) => {
            if (el.classList.contains('nc-in') || !el.hasAttribute('data-nc-reveal')) return false;
            const r = el.getBoundingClientRect();
            return r.top < window.innerHeight && r.bottom > 0;
          });
          row.forEach((el) => {
            const n = counters.get(group) || 0;
            counters.set(group, n + 1);
            observer.unobserve(el);
            reveal(el, Math.min(n, MAX_STAGGER) * STAGGER);
          });
        });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  const prepare = (section) => {
    if (section.matches(SKIP_SECTIONS) || section.querySelector(SKIP_INSIDE)) return;
    units.length = 0;
    collect(section);
    const fold = window.innerHeight;
    units.forEach(({ el, group }) => {
      if (el.hasAttribute('data-nc-reveal') || !el.offsetHeight) return;
      // Déjà visible au chargement : on n'y touche pas
      if (el.getBoundingClientRect().top < fold) return;
      el.__ncGroup = group;
      if (group) members.set(group, [...(members.get(group) || []), el]);
      el.setAttribute('data-nc-reveal', '');
      el.querySelectorAll('img').forEach((img) => {
        const box = img.parentElement && getComputedStyle(img.parentElement);
        if (box && box.overflow === 'hidden') img.setAttribute('data-nc-zoom', '');
      });
      observer.observe(el);
    });
  };

  const init = () => {
    if (!html.dataset.ncMotion) return;
    document.querySelectorAll('main .shopify-section').forEach(prepare);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Éditeur de thème : une section rechargée s'affiche directement, sans animation
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-nc-reveal]').forEach((el) => el.removeAttribute('data-nc-reveal'));
  });
})();
